import { CONSULTATION_STEPS, FEATURE } from '@abiglobalhealth/go-types'
import * as Sentry from '@sentry/browser'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { BackgroundPattern } from '@/components/BackgroundPattern'
import { TabBar } from '@/components/Consultation/components/TabBar'
import { DotsAnimation } from '@/components/DotsAnimation'
import { Loading } from '@/components/Loading'
import { Module } from '@/components/Module'
import { useConfig } from '@/hooks/useConfig'
import { useConsultationEnded } from '@/hooks/useConsultationEnded'
import { useConsultationStep } from '@/hooks/useConsultationStep'
import { useConsultationType } from '@/hooks/useConsultationType'
import { useJourney } from '@/hooks/useJourney'
import { useLoading } from '@/hooks/useLoading'
import { useModule } from '@/hooks/useModule'
import { useStep } from '@/hooks/useStep'
import { useUser } from '@/hooks/useUser'
import i18n from '@/locale/i18n'
import { getTranslationKey } from '@/locale/utils'
import { CaseClosed } from '@/pages/CaseClosed'

function App() {
  const { t } = useTranslation()

  const { initialized } = useJourney()
  const { user } = useUser()
  const { loading } = useLoading()
  const { currentModule } = useModule()
  const { currentStep } = useStep()
  const { updatingConsultationType } = useConsultationType()
  const { currentConsultationStep } = useConsultationStep()
  const consultationEnded = useConsultationEnded()
  const { config } = useConfig()
  const availableServices = config?.consultation?.availableServices
  const externalCreation = config?.features?.includes(FEATURE.GOAPP_EXTERNAL_CASE_CREATION)

  const showCaseClosedScreen = useMemo(
    () => (availableServices?.length === 0 || externalCreation) && consultationEnded && currentConsultationStep !== CONSULTATION_STEPS.ANSWER,
    [availableServices, consultationEnded, currentConsultationStep]
  )

  const translationKey = getTranslationKey()
  const footerKey = `footer.project_number.${translationKey}`
  const footerTranslation = t(footerKey)
  const footerHasTranslation = !!translationKey && footerTranslation !== footerKey

  useEffect(() => {
    Sentry.setTag('language', i18n.language)
  }, [i18n.language])

  return (
    <div className="bg-primary-light flex w-full flex-1 flex-col items-center justify-center sm:p-4">
      <div className="relative flex h-full w-full max-w-160 grow flex-col overflow-clip sm:rounded-lg sm:shadow-lg">
        <div className="relative flex grow flex-col overflow-y-auto">
          <BackgroundPattern className="bg-gray-background flex grow flex-col overflow-auto">
            {showCaseClosedScreen ? (
              <CaseClosed />
            ) : (
              <>
                <TabBar />
                {updatingConsultationType ? (
                  <div className="flex grow items-center justify-center">
                    <DotsAnimation className="text-primary h-96 w-96" />
                  </div>
                ) : (
                  initialized && <Module />
                )}
              </>
            )}
          </BackgroundPattern>
        </div>

        {footerHasTranslation && <div className="bg-primary p-2 text-center text-xs text-white">{footerTranslation}</div>}

        {!import.meta.env.PROD && (
          <div className="flex flex-col bg-white p-2">
            <span className="text-center text-xs">
              <b>User conditions</b> ·{' '}
              {(user?.routeConditions &&
                Object.entries(user?.routeConditions)
                  .map(([key, value]) => `${key}: ${value}`)
                  .join(', ')) ||
                'none'}
            </span>
            <span className="text-center text-xs">
              <b>Current Module</b> · #{currentModule?.id} ({currentModule?.name})
            </span>
            <span className="text-center text-xs">
              <b>Current Step</b> · #{currentStep?.id} ({currentStep?.name})
            </span>
            <span className="text-center text-xs">
              <b>Current consultation step</b> · {currentConsultationStep}
            </span>
          </div>
        )}

        {loading && <Loading />}
      </div>
    </div>
  )
}

export default App
